import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import clsx from 'clsx';
import Joi from 'joi';

import { useGetTunnelMessagesQuery } from 'api/main';
import { useSendExperienceMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { ineligibleSet } from 'reducers/tunnel';
import { useConfirmSuspend, useTunnelChangeHandlers } from 'utils/hooks';
import { boolToStr, formatLabel, isStatusOk, jsonParseSafe, validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { TunnelStep, FormBox } from '../../common';
import {
    CheckboxLabel,
    CustomTextField,
    ErrorMessage,
    InfoButton,
    RadioLabel,
    SubmitButton,
} from 'components';
import { Box, FormControl, FormGroup, Grid, RadioGroup, Typography } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const optionValidationMessages = {
    'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
    'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
};

const useStyles = makeStyles(theme => ({
    flexContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        display: 'flex',
        columnGap: 16,
    },
    optionsContainer: {
        display: 'flex',
        flexDirection: 'row',
        marginLeft: 20,
        [theme.breakpoints.down(520)]: {
            alignItems: 'flex-start',
            flexDirection: 'column',
        },
    },
    option: {
        fontSize: 16,
        color: theme.palette.grey[600],
        alignItems: 'baseline',
        display: 'table',
        '& .MuiFormControlLabel-label': {
            display: 'table-cell',
        },
    },
    optionLabel: {
        color: theme.palette.grey[600],
        fontSize: 16,
    },
    subLabel: {
        marginBottom: 10,
        fontSize: 16,
    },
    radioGroup: {
        flexDirection: 'column',
        display: 'flex',
    },
    subQuestion: {
        marginTop: theme.spacing(2),
        [theme.breakpoints.up(500)]: {
            marginLeft: 80,
        },
    },
    label: {
        width: 175,
        marginRight: 10,
        marginBottom: 0,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    infoButton: { marginRight: 24 },
    instrumentsContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 10,
    },
}));

const ExperienceFCPR = props => {
    const {
        getCompletedFormTitle,
        userStep,
        handleNextStep,
        kycSurvey,
        defaultForm_1: defaultForm_2,
    } = props;

    const setTunnelChangesHandlers = useTunnelChangeHandlers();
    const { data: messages } = useGetTunnelMessagesQuery();
    const [sendExperience, { isLoading: buttonLoader, error }] = useSendExperienceMutation();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const userAdequacy = useMemo(() => tunnelSubscription?.userAdequacy, [tunnelSubscription]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const hasSuccessfulAttempt = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const handleConfirmSuspend = useConfirmSuspend();

    const dispatch = useDispatch();
    const classes = useStyles();

    const isResubmit = useMemo(
        () => userStep?.successfulAttemptsNumber > 0 || userStep?.failedAttemptsNumber > 0,
        [userStep]
    );
    const [currentForm, setCurrentForm] = useState(0);
    const [formStatus, setFormStatus] = useState([
        {
            status: 'started',
            isDirty: false,
            formKey: 'form_0',
            errors: {},
        },
        {
            status: 'notStarted',
            isDirty: false,
            formKey: 'form_1',
            errors: {},
        },
        {
            status: 'notStarted',
            isDirty: false,
            formKey: 'form_2',
            errors: {},
        },
        {
            status: 'notStarted',
            isDirty: false,
            formKey: 'form_3',
            errors: {},
        },
        {
            status: 'notStarted',
            isDirty: false,
            formKey: 'form_4',
            errors: {},
        },
    ]);

    const formTouched = useCallback(() => {
        setFormStatus(prevState => [
            ...prevState.slice(0, currentForm),
            { ...prevState[currentForm], isDirty: true },
            ...prevState.slice(currentForm + 1),
        ]);
    }, [currentForm]);

    const defaultForm_0 = useMemo(
        () => ({
            question6: isResubmit ? userAdequacy?.question6 : '',
            question7: isResubmit ? userAdequacy?.question7 : '',
            question8: boolToStr(userAdequacy?.question8, 'Oui', 'Non'),
            question9: isResubmit ? userAdequacy?.question9 : '',
            question10: isResubmit ? userAdequacy?.question10 : '',
        }),
        [userAdequacy, isResubmit]
    );
    const [form_0, setForm_0] = useState(defaultForm_0);
    const handleForm_0 = useCallback(
        key => e => {
            setForm_0(prevState => ({ ...prevState, [key]: e.target.value }));
        },
        []
    );

    const defaultForm_1 = useMemo(() => {
        if (kycSurvey && kycSurvey.transactionsCarriedOut !== null) {
            return kycSurvey.transactionsCarriedOut;
        }
        return '';
    }, [kycSurvey]);
    const [form_1, setForm_1] = useState(defaultForm_1);
    const handleForm_1 = useCallback(e => setForm_1(e.target.value), []);

    const [form_2, setForm_2] = useState(defaultForm_2);
    const handleForm_2 = useCallback(
        key =>
            ({ target: { name, checked, value } }) => {
                setForm_2(prevState => {
                    if (key !== 'choice') {
                        return { ...prevState, other: value };
                    } else {
                        return {
                            choice: {
                                ...prevState.choice,
                                [name]: checked,
                            },
                            other:
                                !checked && name === 'step36.question_2.option_4'
                                    ? ''
                                    : prevState.other,
                        };
                    }
                });
                formTouched();
            },
        [formTouched]
    );

    const defaultForm_3 = useMemo(() => {
        const financialInstrument = jsonParseSafe(kycSurvey?.financialInstrument);
        const form_3_state = {
            choice: {
                'step36.question_1.option_1': false,
                'step36.question_1.option_2': false,
                'step36.question_1.option_3': false,
                'step36.question_1.option_4': false,
                'step36.question_1.option_5': false,
                'step36.question_1.option_6': false,
                'step36.question_1.option_7': false,
                'general_options.other': false,
                'general_options.none': false,
            },
            other: '',
        };

        if (financialInstrument) {
            Object.keys(form_3_state.choice).forEach(key => {
                form_3_state.choice[key] = financialInstrument.choice.indexOf(key) > -1;
            });

            form_3_state.other = financialInstrument.other;
        }

        return form_3_state;
    }, [kycSurvey]);
    const [form_3, setForm_3] = useState(defaultForm_3);
    const handleForm_3 = useCallback(
        key =>
            ({ target: { name, checked, value } }) => {
                setForm_3(prevState => {
                    if (key === 'none') {
                        return {
                            ...prevState,
                            choice: {
                                'step36.question_1.option_7': false,
                                'step36.question_1.option_6': false,
                                'step36.question_1.option_5': false,
                                'step36.question_1.option_4': false,
                                'step36.question_1.option_3': false,
                                'step36.question_1.option_2': false,
                                'step36.question_1.option_1': false,
                                'general_options.none': checked,
                                'general_options.other': false,
                            },
                            other: '',
                        };
                    } else {
                        return {
                            ...prevState,
                            [key]:
                                key === 'choice'
                                    ? {
                                          ...prevState.choice,
                                          'general_options.none': false,
                                          [name]: checked,
                                      }
                                    : value,
                        };
                    }
                });
                formTouched();
            },
        [formTouched]
    );
    const validateForm_3 = useCallback(() => {
        const isValid = Object.keys(form_3.choice)
            .slice(0, 7)
            .some(option => form_3.choice[option] === true);

        if (form_3.choice['general_options.other']) {
            return !!form_3.other;
        } else if (form_3.choice['general_options.none'] && !form_3.other && !isValid) {
            return true;
        } else {
            return isValid;
        }
    }, [form_3]);

    const defaultForm_4 = useMemo(
        () => ({
            opcvmActionInvestment: isResubmit ? kycSurvey?.opcvmActionInvestment : '',
            opcvmObligationInvestment: isResubmit ? kycSurvey?.opcvmObligationInvestment : '',
            opcvmMonetaryInvestment: isResubmit ? kycSurvey?.opcvmMonetaryInvestment : '',
            listedShareInvestment: isResubmit ? kycSurvey?.listedShareInvestment : '',
            unlistedShareInvestment: isResubmit ? kycSurvey?.unlistedShareInvestment : '',
            otherFundInvestment: isResubmit ? kycSurvey?.otherFundInvestment : '',
            otherInvestment: isResubmit ? kycSurvey?.otherInvestment : '',
        }),
        [kycSurvey, isResubmit]
    );
    const [form_4, setForm_4] = useState(defaultForm_4);
    const handleForm_4 = useCallback(
        key =>
            ({ target: { value } }) => {
                setForm_4(prevState => ({ ...prevState, [key]: value }));
                formTouched();
            },
        [formTouched]
    );

    const schema = useMemo(
        () =>
            tunnelSubscription.indirectUntransferred
                ? {}
                : {
                      form_0: {
                          question6: Joi.string().required().messages(optionValidationMessages),
                          question7: Joi.string().required().messages(optionValidationMessages),
                          question8: Joi.boolean().required().truthy('Oui').falsy('Non').messages({
                              'boolean.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                          }),
                          question9: Joi.string().required().messages(optionValidationMessages),
                          question10: Joi.string().required().messages(optionValidationMessages),
                      },
                      form_1: Joi.number().min(0).required().messages({
                          'number.base': texts.FIELD_REQUIRED,
                          'number.min': texts.FIELD_REQUIRED,
                      }),
                      form_2: {
                          other: form_2.choice['step36.question_2.option_4']
                              ? Joi.string().required()
                              : Joi.any(),
                      },
                      form_4: {
                          opcvmActionInvestment: Joi.string().required(),
                          opcvmObligationInvestment: Joi.string().required(),
                          opcvmMonetaryInvestment: Joi.string().required(),
                          listedShareInvestment: Joi.string().required(),
                          unlistedShareInvestment: Joi.string().required(),
                          otherFundInvestment: Joi.string().required(),
                          otherInvestment: Joi.string().required(),
                      },
                  },
        [tunnelSubscription, form_2]
    );

    // enable submit button if last form is valid
    useEffect(() => {
        if (currentFormIsValid(schema['form_4'], form_4, 'form_4')) {
            setIsSubmitDisabled(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form_4]);

    const formList = useMemo(
        () => [form_0, form_1, form_2, form_3, form_4],
        [form_0, form_1, form_2, form_3, form_4]
    );
    const defaultFormList = useMemo(
        () => [defaultForm_0, defaultForm_1, defaultForm_2, defaultForm_3, defaultForm_4],
        [defaultForm_0, defaultForm_3, defaultForm_1, defaultForm_2, defaultForm_4]
    );

    const currentFormIsValid = useCallback(
        (formSchema, form, formKey) => {
            if (tunnelSubscription.indirectUntransferred) return true;
            const index = formStatus.findIndex(x => x.formKey === formKey);
            const { valid, newErrors } = validateSchema(formSchema, form);
            let isValid = valid;
            let errors = formKey === 'form_1' ? { form_1: newErrors } : { ...newErrors };

            if (!isValid && formKey === 'form_2') {
                if (form.choice['step36.question_2.option_4'] && !form.other) {
                    errors[formKey] = {
                        other: texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                    };
                }
            }

            if (!isValid && formKey === 'form_4') {
                errors['radio'] = texts.CHOISIR_UNE_OPTION;
            }

            if (formKey === 'form_3') {
                isValid = validateForm_3();
            }

            if (!isValid && formKey === 'form_3') {
                errors[formKey] = {
                    ...errors,
                    checkbox: texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                };
                if (form.choice['general_options.other'] && !form.other) {
                    errors[formKey] = {
                        input: texts.REMPLIER_CETT_ENTREE,
                    };
                }
            }

            setFormStatus(prevState => [
                ...prevState.slice(0, index),
                {
                    ...prevState[index],
                    errors: isValid ? {} : errors,
                },
                ...prevState.slice(index + 1),
            ]);

            return isValid;
        },
        [validateForm_3, formStatus, tunnelSubscription]
    );

    const handleFormBox = useCallback(
        key => () => {
            let untouchedFormIndex = formStatus.findIndex(form => form.status === 'notStarted');
            const formIndex = formStatus.findIndex(x => x.formKey === key);
            untouchedFormIndex = untouchedFormIndex !== -1 ? untouchedFormIndex : formIndex + 1;
            if (currentFormIsValid(schema[key], formList[formIndex], key)) {
                setCurrentForm(untouchedFormIndex);
                setFormStatus(prevState => {
                    if (untouchedFormIndex !== -1) {
                        return [
                            ...prevState.slice(0, formIndex),
                            { ...prevState[formIndex], status: 'completed', isDirty: true },
                            ...prevState.slice(formIndex + 1, untouchedFormIndex),
                            {
                                ...prevState[untouchedFormIndex],
                                isDirty: false,
                                status: 'started',
                            },
                            ...prevState.slice(untouchedFormIndex + 1),
                        ];
                    } else {
                        return [
                            ...prevState.slice(0, formIndex),
                            { ...prevState[formIndex], status: 'completed', isDirty: true },
                            {
                                ...prevState[formIndex + 1],
                                isDirty: true,
                                status: 'started',
                            },
                            ...prevState.slice(formIndex + 2),
                        ];
                    }
                });
            }
        },
        [currentFormIsValid, formStatus, schema, formList]
    );

    const everyFormIsValid = useCallback(() => {
        if (tunnelSubscription.indirectUntransferred) return true;
        return formStatus.every((form, i) =>
            currentFormIsValid(schema[form.formKey], formList[i], form.formKey)
        );
    }, [currentFormIsValid, formStatus, formList, schema, tunnelSubscription]);

    const handleEditFormBox = key => () => {
        const formId = formStatus.findIndex(x => x.formKey === key); // ID of the form that is clicked
        const currentFormKey = formStatus[currentForm].formKey;
        const allFormsCompleted = !formStatus.find(x => x.status !== 'completed');
        if (allFormsCompleted) {
            setFormStatus(prevState => {
                return [
                    ...prevState.slice(0, formId),
                    {
                        ...prevState[formId],
                        status: 'started',
                        isDirty: true,
                    },
                    ...prevState.slice(formId + 1),
                ];
            });
            setCurrentForm(formId);
            return;
        }

        if (currentFormIsValid(schema[currentFormKey], formList[currentForm], currentFormKey)) {
            setFormStatus(prevState => {
                if (formId > currentForm) {
                    return [
                        ...prevState.slice(0, currentForm),
                        {
                            ...prevState[currentForm],
                            status: 'completed',
                            isDirty: true,
                        },
                        ...prevState.slice(currentForm + 1, formId),
                        { ...prevState[formId], status: 'started', isDirty: true },
                        ...prevState.slice(formId + 1),
                    ];
                } else {
                    return [
                        ...prevState.slice(0, formId),
                        {
                            ...prevState[formId],
                            status: 'started',
                            isDirty: true,
                        },
                        ...prevState.slice(formId + 1, currentForm),
                        { ...prevState[currentForm], status: 'completed', isDirty: true },
                        ...prevState.slice(currentForm + 1),
                    ];
                }
            });
            setCurrentForm(formId);
        }
    };

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            if (everyFormIsValid()) {
                sendExperience({
                    payload: {
                        form_0: form_3,
                        form_1: form_2,
                        form_2: form_4,
                        form_3: form_1,
                        form_4: form_0,
                    },
                    callback: () => {
                        if (afterSubmitCallback) {
                            afterSubmitCallback();
                        } else {
                            handleNextStep(true);
                        }
                    },
                });
            }
        },
        [everyFormIsValid, sendExperience, form_3, form_2, form_4, form_1, form_0, handleNextStep]
    );

    const checkChanges = useCallback(() => {
        return !_.isEqualWith(defaultFormList, formList, (objValue, othValue) => {
            return objValue.every((formObject, i) => _.isEqual(formObject, othValue[i]));
        });
    }, [formList, defaultFormList]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, hasSuccessfulAttempt),
        [handleSubmit, checkChanges, hasSuccessfulAttempt, setTunnelChangesHandlers]
    );

    // set form to completed if resubmit
    useEffect(() => {
        if (isResubmit) {
            setFormStatus(prevState =>
                prevState.reduce(
                    (acc, value) => [...acc, { ...value, isDirty: true, status: 'completed' }],
                    []
                )
            );
        }
    }, [isResubmit, defaultFormList]);

    useEffect(() => {
        if (error?.status === 422) {
            dispatch(
                ineligibleSet({
                    open: true,
                    abandon: false,
                    canConfirm: true,
                    message: texts.VOTRE_RESPONSE_NE_SEMBLENT,
                    confirmMessage: texts.PAUSE_AND_WAIT,
                    onConfirm: handleConfirmSuspend,
                })
            );
        }
    }, [dispatch, error?.status, handleConfirmSuspend]);

    return (
        <TunnelStep
            title={texts.KNOWLEDGE_AND_EXPERIENCE}
            subtitle={texts.EXPERIENCE_SUBTITLE_FCPR}
        >
            <Grid container direction="column" justifyContent="center">
                <FormBox
                    handleEditFormBox={handleEditFormBox('form_0')}
                    handleFormBox={handleFormBox('form_0')}
                    status={formStatus[0].status}
                    showInfoButton={false}
                    label={texts.COMPREHENSION_DU_PRODUIT}
                    completedLabel={texts.COMPREHENSION_DU_PRODUIT}
                    largeLabel
                >
                    <Box mb="20px">
                        <label className={classes.questionLabel}>
                            {formatLabel(messages['step37.question_6.label'])}
                        </label>
                        <RadioGroup
                            className={classes.flexContainer}
                            onChange={handleForm_0('question6', setForm_1)}
                            value={form_0.question6}
                        >
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.yes']}
                                value={'general_options.yes'}
                                checked={form_0.question6 === 'general_options.yes'}
                            />
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.no']}
                                value={'general_options.no'}
                                checked={form_0.question6 === 'general_options.no'}
                            />
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.unknown']}
                                value={'general_options.unknown'}
                                checked={form_0.question6 === 'general_options.unknown'}
                            />
                        </RadioGroup>
                        {!!formStatus[1].errors?.question6 && (
                            <ErrorMessage
                                className={classes.error}
                                message={formStatus[1].errors?.question6}
                            />
                        )}
                    </Box>
                    <Box mb="20px">
                        <label className={classes.questionLabel}>
                            {formatLabel(messages['step37.question_7.label'])}
                        </label>
                        <RadioGroup
                            className={classes.flexContainer}
                            onChange={handleForm_0('question7', setForm_1)}
                            value={form_0.question7}
                        >
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.yes']}
                                value={'general_options.yes'}
                                checked={form_0.question7 === 'general_options.yes'}
                            />
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.no']}
                                value={'general_options.no'}
                                checked={form_0.question7 === 'general_options.no'}
                            />
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.unknown']}
                                value={'general_options.unknown'}
                                checked={form_0.question7 === 'general_options.unknown'}
                            />
                        </RadioGroup>
                        {!!formStatus[1].errors?.question7 && (
                            <ErrorMessage
                                className={classes.error}
                                message={formStatus[1].errors?.question7}
                            />
                        )}
                    </Box>
                    <Box mb="20px">
                        <label className={classes.questionLabel}>
                            {messages['step37.question_8.label']}
                        </label>
                        <RadioGroup
                            className={classes.flexContainer}
                            onChange={handleForm_0('question8', setForm_1)}
                            value={form_0.question8}
                        >
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.yes']}
                                value={messages['general_options.yes']}
                                checked={form_0.question8 === 'Oui'}
                            />
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.no']}
                                value={messages['general_options.no']}
                                checked={form_0.question8 === 'Non'}
                            />
                        </RadioGroup>
                        {!!formStatus[1].errors?.question8 && (
                            <ErrorMessage
                                className={classes.error}
                                message={formStatus[1].errors?.question8}
                            />
                        )}
                    </Box>
                    <Box mb="20px">
                        <label className={classes.questionLabel}>
                            {formatLabel(
                                messages['step37.question_9.label'].replace(
                                    '%sgp_name%',
                                    tunnelSubscription?.investmentFund?.sgp?.name
                                )
                            )}
                        </label>
                        <RadioGroup
                            className={classes.flexContainer}
                            onChange={handleForm_0('question9', setForm_1)}
                            value={form_0.question9}
                        >
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.yes']}
                                value={'general_options.yes'}
                                checked={form_0.question9 === 'general_options.yes'}
                            />
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.no']}
                                value={'general_options.no'}
                                checked={form_0.question9 === 'general_options.no'}
                            />
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.unknown']}
                                value={'general_options.unknown'}
                                checked={form_0.question9 === 'general_options.unknown'}
                            />
                        </RadioGroup>
                        {!!formStatus[1].errors?.question9 && (
                            <ErrorMessage
                                className={classes.error}
                                message={formStatus[1].errors?.question9}
                            />
                        )}
                    </Box>
                    <Box mb="20px">
                        <label className={classes.questionLabel}>
                            {formatLabel(messages['step37.question_10.label'])}
                        </label>
                        <RadioGroup
                            className={classes.flexContainer}
                            onChange={handleForm_0('question10', setForm_1)}
                            value={form_0.question10}
                        >
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.yes']}
                                value={'general_options.yes'}
                                checked={form_0.question10 === 'general_options.yes'}
                            />
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.no']}
                                value={'general_options.no'}
                                checked={form_0.question10 === 'general_options.no'}
                            />
                            <RadioLabel
                                className={classes.option}
                                label={messages['general_options.unknown']}
                                value={'general_options.unknown'}
                                checked={form_0.question10 === 'general_options.unknown'}
                            />
                        </RadioGroup>
                        {!!formStatus[0].errors?.question10 && (
                            <ErrorMessage
                                className={classes.error}
                                message={formStatus[0].errors?.question10}
                            />
                        )}
                    </Box>
                </FormBox>
                <FormBox
                    status={formStatus[1].status}
                    label={messages['step36.question_transactions_carried_out.label']}
                    completedLabel={
                        <span>
                            {texts.TRANSACTIONS_EXPERIENCE} <strong>{form_1}</strong>
                        </span>
                    }
                    handleFormBox={handleFormBox('form_1')}
                    handleEditFormBox={handleEditFormBox('form_1')}
                    showInfoButton={false}
                >
                    <CustomTextField
                        onChange={handleForm_1}
                        value={form_1}
                        type="number"
                        showError={!!formStatus[1].errors['form_1']}
                        error={formStatus[1].errors['form_1']}
                    />
                </FormBox>
                <FormBox
                    handleEditFormBox={handleEditFormBox('form_2')}
                    handleFormBox={handleFormBox('form_2')}
                    showInfoButton={false}
                    status={formStatus[2].status}
                    label={messages['step36.question_2.label']}
                    completedLabel={getCompletedFormTitle(form_2, 'form_36')}
                >
                    <FormGroup className={classes.flexContainer}>
                        {[1, 2, 3].map(i => (
                            <CheckboxLabel
                                className={classes.option}
                                name={`step36.question_2.option_${i}`}
                                key={`step36.question_2.option_${i}`}
                                onChange={handleForm_2('choice')}
                                label={messages[`step36.question_2.option_${i}.label`]}
                                checked={form_2.choice[`step36.question_2.option_${i}`]}
                            />
                        ))}
                        {form_2.choice['step36.question_2.option_4'] && (
                            <FormControl component="fieldset" className={classes.subQuestion}>
                                <label className={classes.subLabel}>
                                    {messages['step36.question_2.option_4.sub_label']}
                                </label>
                                <RadioGroup
                                    value={form_2.other}
                                    onChange={handleForm_2('other')}
                                    className={classes.radioGroup}
                                >
                                    {[1, 2, 3].map(i => (
                                        <RadioLabel
                                            key={i}
                                            labelPlacement="end"
                                            value={`step36.question_2.option_4.option_${i}`}
                                            label={
                                                messages[`step36.question_2.option_4.option_${i}`]
                                            }
                                        />
                                    ))}
                                </RadioGroup>
                                {!!formStatus[2].errors['form_2']?.other && (
                                    <ErrorMessage message={formStatus[2].errors['form_2'].other} />
                                )}
                            </FormControl>
                        )}
                        {!!formStatus[2].errors['form_2']?.checkbox && (
                            <ErrorMessage message={formStatus[2].errors['form_2'].checkbox} />
                        )}
                    </FormGroup>
                </FormBox>
                <FormBox
                    status={formStatus[3].status}
                    label={messages['step36.question_1.label']}
                    infoContent={texts.INSTRUMENTS_FINANCIERS_INFO}
                    infoTitle={texts.INSTRUMENTS_FINANCIERS}
                    completedLabel={getCompletedFormTitle(form_3, 'form_4')}
                    handleEditFormBox={handleEditFormBox('form_3')}
                    handleFormBox={handleFormBox('form_3')}
                >
                    <FormGroup className={classes.flexContainer}>
                        {[1, 2, 3, 4, 5, 6, 7].map(i => (
                            <React.Fragment key={`step36.question_1.option_${i}`}>
                                <Box display="flex" flexDirection="row" flexWrap="nowrap">
                                    <CheckboxLabel
                                        name={`step36.question_1.option_${i}`}
                                        className={classes.option}
                                        label={
                                            <React.Fragment>
                                                {messages[`step36.question_1.option_${i}`]}
                                                {[1, 2, 3].includes(i) && (
                                                    <InfoButton
                                                        disableMarginBottom
                                                        className={classes.infoButton}
                                                        title={i === 1 ? texts.OPCVM : texts.FIA}
                                                        content={
                                                            i === 1
                                                                ? texts.PRODUIT_FINANCIER_AYANT
                                                                : texts.CAPITAUX_AUPRES
                                                        }
                                                    />
                                                )}
                                            </React.Fragment>
                                        }
                                        onChange={handleForm_3('choice')}
                                        checked={form_3.choice[`step36.question_1.option_${i}`]}
                                    />
                                </Box>
                            </React.Fragment>
                        ))}
                        <CheckboxLabel
                            className={classes.option}
                            label={messages['general_options.none']}
                            name="general_options.none"
                            onChange={handleForm_3('none')}
                            checked={form_3.choice['general_options.none']}
                        />
                        <CheckboxLabel
                            name="general_options.other"
                            label={messages['general_options.other']}
                            className={classes.option}
                            onChange={handleForm_3('choice')}
                            checked={form_3.choice['general_options.other']}
                        />
                        {form_3.choice['general_options.other'] && (
                            <CustomTextField
                                onChange={handleForm_3('other')}
                                value={form_3.other}
                                showError={!!formStatus[3].errors['form_3']?.input}
                                error={formStatus[3].errors['form_3']?.input}
                            />
                        )}
                    </FormGroup>
                    {!!formStatus[3].errors['form_3']?.checkbox && (
                        <ErrorMessage message={formStatus[3].errors['form_3'].checkbox} />
                    )}
                </FormBox>
                <FormBox
                    isLast
                    handleEditFormBox={handleEditFormBox('form_4')}
                    handleFormBox={handleFormBox('form_4')}
                    infoTitle={texts.INVESTISSEMENTS_REALISES}
                    infoContent={texts.INVESTISSEMENTS_REALISES_INFO}
                    status={formStatus[4].status}
                    label={messages['step36.question_3.label']}
                    completedLabel={getCompletedFormTitle(form_4, 'form_5')}
                >
                    {Object.keys(form_4).map((formState, i) => (
                        <RadioGroup
                            key={formState}
                            className={classes.instrumentsContainer}
                            onChange={handleForm_4(formState)}
                        >
                            <Typography
                                className={clsx(classes.label, classes.optionLabel)}
                                variant="body1"
                                gutterBottom
                            >
                                {messages[`step36.question_${i + 4}.label`]}
                            </Typography>
                            <div className={classes.optionsContainer}>
                                <RadioLabel
                                    className={classes.option}
                                    value="financial_instruments_options.no_orders"
                                    label={messages['financial_instruments_options.no_orders']}
                                    checked={
                                        form_4[formState] ===
                                        'financial_instruments_options.no_orders'
                                    }
                                />
                                <RadioLabel
                                    className={classes.option}
                                    value="financial_instruments_options.between_1_and_3_orders"
                                    label={
                                        messages[
                                            'financial_instruments_options.between_1_and_3_orders'
                                        ]
                                    }
                                    checked={
                                        form_4[formState] ===
                                        'financial_instruments_options.between_1_and_3_orders'
                                    }
                                />
                                <RadioLabel
                                    className={classes.option}
                                    value="financial_instruments_options.more_than_3_orders"
                                    label={
                                        messages['financial_instruments_options.more_than_3_orders']
                                    }
                                    checked={
                                        form_4[formState] ===
                                        'financial_instruments_options.more_than_3_orders'
                                    }
                                />
                            </div>
                        </RadioGroup>
                    ))}
                </FormBox>
            </Grid>
            <SubmitButton
                loading={buttonLoader}
                text={hasSuccessfulAttempt && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                disabled={isSubmitDisabled || buttonLoader}
                onClick={changesMade || !hasStatusOk ? handleSubmit() : handleNextStep}
            />
        </TunnelStep>
    );
};

export default ExperienceFCPR;
